/**
 * KINETIC FIDELITY ENGINE
 * Based on "The Physics of Legitimacy" Research Paper
 *
 * Implements the Damped Harmonic Oscillator conversion formulas:
 * k (stiffness) = m * (2π / T)²
 * c (damping)   = 4π * ζ * m / T
 *
 * Where:
 * - T = Response time (period) in seconds
 * - ζ = Damping ratio (1.0 = critical, <1.0 = underdamped/bouncy)
 * - m = Mass (typically 1)
 */

type SpringConfig = {
  type: 'spring';
  stiffness: number;
  damping: number;
  mass: number;
  restDelta: number;
  restSpeed: number;
};

/**
 * Convert iOS-style response time and damping ratio to Framer Motion spring physics
 * This is the core formula from Section 2.1 of the research paper
 */
const solveSpring = (response: number, dampingRatio: number, mass: number = 1): SpringConfig => {
  // Prevent division by zero errors from edge cases
  const safeResponse = Math.max(response, 0.05);

  // k = m * (2π / T)²
  const stiffness = mass * Math.pow((2 * Math.PI) / safeResponse, 2);

  // c = 4π * ζ * m / T
  const damping = (4 * Math.PI * dampingRatio * mass) / safeResponse;

  return {
    type: 'spring',
    stiffness,
    damping,
    mass,
    // Research Section 2.3: "iOS settles very precisely"
    restDelta: 0.001,
    restSpeed: 0.001,
  };
};

export const iOS_SPRINGS = {
  /**
   * SYSTEM DEFAULT (Navigation Push/Pop)
   * Research Table 1: Response 0.55s, Damping 1.0
   * Result: stiffness ~130.5, damping ~22.8
   * Feel: "Heavy", no bounce, decisive.
   */
  slide: { ...solveSpring(0.55, 1.0) },

  /**
   * SHEET PRESENTATION (Modal Slide Up)
   * Research Table 1: Response 0.45s, Damping 0.85
   * Feel: Slightly faster, tiny overshoot to signal arrival.
   */
  overlay: { ...solveSpring(0.45, 0.85) },

  /**
   * SNAPPY INTERACTION (Toggle / Tab Switch)
   * Research Table 1: Response 0.35s, Damping 0.75
   * Feel: Tight, responsive, rubbery.
   */
  snappy: { ...solveSpring(0.35, 0.75) },

  /**
   * BUTTON SQUISH (Press Down)
   * Research Section 4.3: Response 0.2s, Damping 1.0 (Overdamped)
   * Feel: Instant compression, no oscillation under finger.
   */
  buttonPress: { ...solveSpring(0.2, 1.0) },

  /**
   * BUTTON RELEASE (Bounce Back)
   * Research Section 4.3: Response 0.35s, Damping 0.6
   * Feel: "Rubber-like" wobble on release.
   */
  buttonRelease: { ...solveSpring(0.35, 0.6) },

  /**
   * RUBBER BAND (Scroll/Drag Resistance)
   * Research Section 6.2: Response 0.4s, Damping 1.0
   */
  rubberBand: { ...solveSpring(0.4, 1.0) },

  /**
   * TAB SWITCH (Dock navigation)
   * Matched to snappy for consistent feel
   */
  tabSwitch: { ...solveSpring(0.35, 0.75) },

  /**
   * DEFAULT (General purpose spring)
   * Balanced middle-ground
   */
  default: { ...solveSpring(0.45, 0.85) },

  /**
   * GENTLE (Slower, gradual animations)
   */
  gentle: { ...solveSpring(0.6, 0.9) },
} as const;

export const EASINGS = {
  standard: 'cubic-bezier(0.22, 1, 0.36, 1)', // Enters and in-place changes
  decelerate: 'cubic-bezier(0, 0, 0.2, 1)', // Arrivals: settle without overshoot
  accelerate: 'cubic-bezier(0.4, 0, 1, 1)', // Exits: leave with intent
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)', // Short, mechanical state flips
} as const;

export const MOTION_EASINGS = {
  standard: [0.22, 1, 0.36, 1],
  decelerate: [0, 0, 0.2, 1],
  accelerate: [0.4, 0, 1, 1],
} as const;

export const DURATIONS = {
  instant: 0, // Immediate feedback
  fast: 150, // Button presses, quick interactions
  normal: 250, // Standard transitions
  slow: 300, // Complex transitions
  slower: 300, // Complex animations
} as const;

/**
 * Common animation configurations for reuse
 */
export const ANIMATIONS = {
  // Button press
  buttonPress: {
    scale: 0.96,
    duration: DURATIONS.fast,
    easing: EASINGS.standard,
  },

  // Button hover
  buttonHover: {
    scale: 1.02,
    duration: DURATIONS.normal,
    easing: EASINGS.standard,
  },

  // Dropdown/modal open
  dropdownOpen: {
    duration: DURATIONS.normal,
    easing: EASINGS.decelerate,
  },

  // Dropdown/modal close
  dropdownClose: {
    duration: DURATIONS.fast,
    easing: EASINGS.accelerate,
  },

  // Page transitions
  pageTransition: {
    duration: DURATIONS.normal,
    easing: EASINGS.standard,
  },

  // Micro-interactions (hover, focus)
  microInteraction: {
    duration: DURATIONS.fast,
    easing: EASINGS.standard,
  },

  // Tab navigation
  tabSwitch: {
    duration: DURATIONS.normal,
    easing: EASINGS.standard,
  },
} as const;

/**
 * Framer Motion variants for common animations
 */
export const MOTION_VARIANTS = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: DURATIONS.normal / 1000, ease: MOTION_EASINGS.standard },
  },

  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: DURATIONS.normal / 1000, ease: MOTION_EASINGS.standard },
  },

  scaleIn: {
    initial: { opacity: 0, scale: 0.98 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.98 },
    transition: { duration: DURATIONS.fast / 1000, ease: MOTION_EASINGS.standard },
  },

  // Stagger children animation
  staggerContainer: {
    animate: {
      transition: {
        staggerChildren: 0.04, // 40ms delay between children
      },
    },
  },
} as const;

/**
 * Helper function to create consistent transition strings for CSS
 */
export function createTransition(
  properties: string[],
  duration: number = DURATIONS.normal,
  easing: string = EASINGS.standard,
): string {
  return properties.map((prop) => `${prop} ${duration}ms ${easing}`).join(', ');
}

/**
 * Helper for Framer Motion transition objects
 */
export function createMotionTransition(
  duration: number = DURATIONS.normal,
  easing: string = EASINGS.standard,
) {
  // Convert CSS cubic-bezier to array format for Framer Motion
  const easingMatch = easing.match(/cubic-bezier\(([\d.]+),\s*([\d.]+),\s*([\d.]+),\s*([\d.]+)\)/);
  const easingArray = easingMatch
    ? [
        parseFloat(easingMatch[1]),
        parseFloat(easingMatch[2]),
        parseFloat(easingMatch[3]),
        parseFloat(easingMatch[4]),
      ]
    : [...MOTION_EASINGS.standard];

  return {
    duration: duration / 1000, // Convert to seconds
    ease: easingArray,
  };
}
